import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Tooltip,
  Badge,
  Collapse,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  BankOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface Estabelecimento {
  cnpj_completo: string;
  ordem: string;
  tipo: string;
  razao_social: string;
  nome_fantasia?: string;
  situacao: string;
  municipio: string;
  uf: string;
  capital_social?: number;
  porte?: string;
  cnae_principal?: string;
  cnae_descricao?: string;
  telefone?: string;
  email?: string;
}

interface DividaEstabelecimento {
  total_dividas: number;
  valor_total: number;
  tipos: string[];
  tipo_estabelecimento: string;
}

interface EstabelecimentosData {
  cnpj_basico: string;
  cnpj_consultado: string;
  matriz: Estabelecimento | null;
  filiais: Estabelecimento[];
  total_estabelecimentos: number;
  dividas_por_estabelecimento: Record<string, DividaEstabelecimento>;
}

interface EstabelecimentosCardProps {
  cnpj: string;
  onSelectCNPJ?: (cnpj: string) => void;
}

const EstabelecimentosCard: React.FC<EstabelecimentosCardProps> = ({ cnpj, onSelectCNPJ }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EstabelecimentosData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEstabelecimentos();
  }, [cnpj]);

  const fetchEstabelecimentos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_SERVIDOR2.BASE}/api/v1/estabelecimentos/${cnpj}`);
      setData(response.data);
    } catch (err) {
      console.error('Erro ao buscar estabelecimentos:', err);
      setError('Erro ao carregar estabelecimentos');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj || cnpj.length < 14) return cnpj;
    const clean = cnpj.replace(/\D/g, '');
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spin size="small" />
          <div style={{ marginTop: 8 }}>Carregando estabelecimentos...</div>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return null;
  }

  const { matriz, filiais, dividas_por_estabelecimento, total_estabelecimentos } = data;
  
  // Calcular totais de dívidas
  let totalDividas = 0;
  let valorTotalDividas = 0;
  Object.values(dividas_por_estabelecimento).forEach(div => {
    totalDividas += div.total_dividas;
    valorTotalDividas += div.valor_total;
  });

  const renderEstabelecimento = (est: Estabelecimento, isMatriz: boolean) => {
    const cnpjLimpo = est.cnpj_completo?.replace(/\D/g, '') || '';
    const dividas = dividas_por_estabelecimento[cnpjLimpo] || dividas_por_estabelecimento[data.cnpj_basico];
    
    return (
      <div 
        key={est.cnpj_completo}
        style={{ 
          padding: '12px',
          background: isMatriz ? '#f6ffed' : '#fafafa',
          borderRadius: 8,
          marginBottom: 8,
          border: isMatriz ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
          cursor: onSelectCNPJ ? 'pointer' : 'default',
        }}
        onClick={() => onSelectCNPJ && onSelectCNPJ(est.cnpj_completo)}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              {isMatriz ? (
                <BankOutlined style={{ color: '#52c41a', fontSize: 16 }} />
              ) : (
                <ShopOutlined style={{ color: '#1890ff', fontSize: 16 }} />
              )}
              <Tag color={isMatriz ? 'green' : 'blue'}>{est.tipo}</Tag>
              <Text strong style={{ fontSize: 13 }}>
                {formatCNPJ(est.cnpj_completo)}
              </Text>
            </Space>
            <Tag color={est.situacao === 'ATIVA' ? 'green' : 'red'}>
              {est.situacao === 'ATIVA' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              {' '}{est.situacao || 'N/A'}
            </Tag>
          </div>
          
          <Text style={{ fontSize: 12 }}>
            {est.razao_social || est.nome_fantasia || 'Sem nome'}
          </Text>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              <EnvironmentOutlined /> {est.municipio || '?'}/{est.uf || '?'}
            </Text>
            
            {dividas && dividas.total_dividas > 0 && (
              <Tooltip title={`${dividas.total_dividas} dívidas - ${dividas.tipos?.join(', ')}`}>
                <Tag color="red" style={{ fontSize: 11 }}>
                  <DollarOutlined /> {formatCurrency(dividas.valor_total)}
                </Tag>
              </Tooltip>
            )}
          </div>
          
          {est.capital_social && est.capital_social > 0 && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Capital: {formatCurrency(est.capital_social)}
            </Text>
          )}
        </Space>
      </div>
    );
  };

  return (
    <Card 
      title={
        <Space>
          <BankOutlined />
          <span>Estabelecimentos</span>
          <Badge 
            count={total_estabelecimentos} 
            style={{ backgroundColor: '#1890ff' }}
            overflowCount={99}
          />
        </Space>
      }
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        totalDividas > 0 && (
          <Tooltip title={`${totalDividas} dívidas totais`}>
            <Tag color="red">{formatCurrency(valorTotalDividas)}</Tag>
          </Tooltip>
        )
      }
    >
      {/* Resumo */}
      {total_estabelecimentos > 1 && (
        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <Statistic 
              title="Total" 
              value={total_estabelecimentos} 
              valueStyle={{ fontSize: 18 }}
            />
          </Col>
          <Col span={8}>
            <Statistic 
              title="Matriz" 
              value={matriz ? 1 : 0} 
              valueStyle={{ fontSize: 18, color: '#52c41a' }}
            />
          </Col>
          <Col span={8}>
            <Statistic 
              title="Filiais" 
              value={filiais.length} 
              valueStyle={{ fontSize: 18, color: '#1890ff' }}
            />
          </Col>
        </Row>
      )}

      {/* Matriz */}
      {matriz && renderEstabelecimento(matriz, true)}

      {/* Filiais */}
      {filiais.length > 0 && (
        <Collapse ghost size="small">
          <Panel 
            header={
              <Text type="secondary">
                <ShopOutlined /> {filiais.length} Filiais
              </Text>
            } 
            key="filiais"
          >
            {filiais.map(filial => renderEstabelecimento(filial, false))}
          </Panel>
        </Collapse>
      )}

      {/* Se não tem matriz nem filiais */}
      {!matriz && filiais.length === 0 && (
        <Empty 
          description="Nenhum estabelecimento encontrado" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Dívidas por estabelecimento */}
      {Object.keys(dividas_por_estabelecimento).length > 0 && (
        <div style={{ marginTop: 12, padding: 8, background: '#fff2e8', borderRadius: 4 }}>
          <Text strong style={{ fontSize: 12, color: '#d46b08' }}>
            <DollarOutlined /> Dívidas PGFN por Estabelecimento:
          </Text>
          {Object.entries(dividas_por_estabelecimento).map(([cnpjDiv, info]) => (
            <div key={cnpjDiv} style={{ marginTop: 4 }}>
              <Text style={{ fontSize: 11 }}>
                {cnpjDiv.length >= 14 ? formatCNPJ(cnpjDiv) : `CNPJ ${cnpjDiv}...`}
                {' '}
                <Tag color={info.tipo_estabelecimento === 'MATRIZ' ? 'green' : 'blue'} style={{ fontSize: 10 }}>
                  {info.tipo_estabelecimento}
                </Tag>
                {' '}
                <Text type="danger">{info.total_dividas} dívidas • {formatCurrency(info.valor_total)}</Text>
              </Text>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default EstabelecimentosCard;
